import React, {
  useState, useEffect, useCallback, useRef, useMemo,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiArrowLeft, FiSearch, FiX, FiPrinter, FiPlus, FiUser, FiGlobe,
} from 'react-icons/fi';
import { useAuth } from '../../../context/AuthContext';
import api, { getEmpresaInfo, getMyOpenCashRegister } from '../../../services/api';
import { imprimirVenta, normalizeTicketItem, ticketQtyUnitText } from '../../../utils/imprimirVenta';
import '../../../styles/NuevaVenta.css';

// ── Constantes ───────────────────────────────────────────────────────────────

const OP_TYPES   = ['VENTA INTERNA'];

const PAYMENT_BUTTONS = [
  { key: 'EFECTIVO',               label: 'EFECTIVO',       icon: '💵', cls: 'nv-pay-efectivo'     },
  { key: 'TARJETA CRED. O DEBITO', label: 'TARJETA',        icon: '💳', cls: 'nv-pay-tarjeta'      },
  { key: 'TRANSFERENCIA BANCARIA', label: 'TRANSFERENCIA',  icon: '🏦', cls: 'nv-pay-transferencia' },
  { key: 'PLIN',                   label: 'PLIN',           icon: '📱', cls: 'nv-pay-plin'         },
  { key: 'YAPE',                   label: 'YAPE',           icon: '📱', cls: 'nv-pay-yape'         },
];

const ITEMS_PER_PAGE = 20;

// Debe coincidir con sales.ticket_client_name VARCHAR(150) en la BD
const TICKET_CLIENT_NAME_MAX_LENGTH = 150;

// ── Helpers ──────────────────────────────────────────────────────────────────

function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// El nombre configurado por el negocio para el documento puede variar
// ("NC", "NOTA DE CRÉDITO", "NOTA DE CREDITO ELECTRONICA", con o sin tilde) —
// se identifica por el código corto "NC" en vez de comparar el texto literal,
// que es frágil ante variaciones de acentuación/mayúsculas. Debe coincidir
// con isCreditNoteDocType() de backend/src/models/Sale.js.
function isCreditNoteDocType(documentType) {
  const normalized = String(documentType || '')
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .trim().toUpperCase();
  return normalized === 'NC' || normalized === 'NOTA DE CREDITO';
}

function fmtMoney(v) {
  return parseFloat(v || 0).toFixed(2);
}

function fmtSeq(n) {
  return String(n).padStart(7, '0');
}

// Datos antiguos guardan el tipo de documento en forma larga
// ("DNI - DOC. NACIONAL DE IDENTIDAD"); para mostrar solo se usa el código corto.
function shortDocType(docType) {
  return (docType || '').split(' - ')[0].trim();
}

function numberToWords(amount) {
  const UNIDADES = ['', 'UNO', 'DOS', 'TRES', 'CUATRO', 'CINCO', 'SEIS', 'SIETE', 'OCHO', 'NUEVE'];
  const DECENAS  = ['', 'DIEZ', 'VEINTE', 'TREINTA', 'CUARENTA', 'CINCUENTA', 'SESENTA', 'SETENTA', 'OCHENTA', 'NOVENTA'];
  const ESPECIALES = { 11:'ONCE',12:'DOCE',13:'TRECE',14:'CATORCE',15:'QUINCE',
    16:'DIECISÉIS',17:'DIECISIETE',18:'DIECIOCHO',19:'DIECINUEVE' };
  const CENTENAS = ['','CIENTO','DOSCIENTOS','TRESCIENTOS','CUATROCIENTOS',
    'QUINIENTOS','SEISCIENTOS','SETECIENTOS','OCHOCIENTOS','NOVECIENTOS'];

  function cientos(n) {
    if (n === 0) return '';
    if (n === 100) return 'CIEN';
    const c = Math.floor(n / 100), resto = n % 100;
    const centStr = CENTENAS[c];
    if (resto === 0) return centStr;
    if (ESPECIALES[resto]) return (centStr ? centStr + ' ' : '') + ESPECIALES[resto];
    const dec = Math.floor(resto / 10), uni = resto % 10;
    const parts = [];
    if (centStr) parts.push(centStr);
    if (dec > 0) parts.push(DECENAS[dec]);
    if (uni > 0) parts.push(UNIDADES[uni]);
    return parts.join(' Y ');
  }
  function miles(n) {
    if (n === 0) return '';
    const m = Math.floor(n / 1000), resto = n % 1000;
    const milStr = m === 1 ? 'MIL' : (m > 0 ? cientos(m) + ' MIL' : '');
    const restoStr = cientos(resto);
    return [milStr, restoStr].filter(Boolean).join(' ');
  }
  const entero = Math.floor(amount);
  const centavos = Math.round((amount - entero) * 100);
  const palabras = entero === 0 ? 'CERO' : miles(entero);
  return `${palabras} CON ${String(centavos).padStart(2, '0')}/100 SOLES`;
}

// ── Subcomponente: Ticket de impresión ───────────────────────────────────────

function TicketPreview({ venta, items, payments, empresa }) {
  const total    = parseFloat(venta.total || 0);
  const subtotal = parseFloat(venta.subtotal ?? total);
  const discount = parseFloat(venta.discount || 0);
  return (
    <div className="nv-ticket">
      <div className="nv-ticket-empresa">
        <div className="nv-ticket-empresa-nombre">{empresa?.name || '—'}</div>
        {empresa?.address && <div className="nv-ticket-empresa-linea">{empresa.address}</div>}
      </div>
      <hr className="nv-ticket-sep" />
      <div className="nv-ticket-doc">
        {venta.serie}-{fmtSeq(venta.secuencia)}
      </div>
      <hr className="nv-ticket-sep" />
      <div className="nv-ticket-info">
        <div className="nv-ticket-info-row">
          <span className="nv-tklbl">CLIENTE:</span>
          <span>{venta.client_name || 'CLIENTES VARIOS'}</span>
        </div>
        {venta.document_number && (
          <div className="nv-ticket-info-row">
            <span className="nv-tklbl">DNI/RUC:</span>
            <span>{venta.document_number}</span>
          </div>
        )}
        <div className="nv-ticket-info-row">
          <span className="nv-tklbl">FECHA:</span>
          <span>{new Date().toLocaleString('es-PE', { timeZone: 'America/Lima' })}</span>
        </div>
        <div className="nv-ticket-info-row">
          <span className="nv-tklbl">VENDEDOR:</span>
          <span>{venta.seller}</span>
        </div>
      </div>
      <div className="nv-ticket-items-head">DETALLE DE PRODUCTOS</div>
      {items.map((raw, idx) => {
        const it = normalizeTicketItem(raw);
        return (
          <div key={idx} className="nv-ticket-item-block">
            <div className="nv-ticket-item-name">{it.product_name}</div>
            <div className="nv-ticket-item-line">
              <span>{ticketQtyUnitText(it)} x S/ {fmtMoney(it.unit_price)}</span>
              <span style={{ textAlign: 'right' }}>S/ {fmtMoney(it.total)}</span>
            </div>
          </div>
        );
      })}
      <div className="nv-ticket-totales">
        <div className="nv-ticket-total-row">
          <span>Gravada S/</span><span>{fmtMoney(subtotal)}</span>
        </div>
        {discount > 0 && (
          <div className="nv-ticket-total-row">
            <span>Descuento S/</span><span>-{fmtMoney(discount)}</span>
          </div>
        )}
        <div className="nv-ticket-total-row grand">
          <span>TOTAL S/</span><span>{fmtMoney(total)}</span>
        </div>
      </div>
      <hr className="nv-ticket-sep" />
      <div className="nv-ticket-info" style={{ fontSize: 10.5 }}>
        <div className="nv-ticket-info-row">
          <span className="nv-tklbl">SON:</span>
          <span style={{ fontSize: 9.5, flex: 1 }}>{numberToWords(total)}</span>
        </div>
        <div className="nv-ticket-info-row">
          <span className="nv-tklbl">FORMA PAGO:</span>
          <span>{payments.map(p => p.method).join(', ') || '-'}</span>
        </div>
      </div>
      {venta.notes && (
        <>
          <hr className="nv-ticket-sep" />
          <div className="nv-ticket-info-row">
            <span className="nv-tklbl">NOTAS:</span>
            <span style={{ flex: 1 }}>{venta.notes}</span>
          </div>
        </>
      )}
    </div>
  );
}

// ── Subcomponente: Modal de ticket ───────────────────────────────────────────

function ModalTicket({ venta, items, payments, empresa, onClose, onNuevaVenta }) {
  const handlePrint = (fmt) => {
    imprimirVenta({
      empresa,
      serie: venta.serie,
      secuencia: venta.secuencia,
      client_name: venta.client_name,
      document_number: venta.document_number,
      date: new Date(),
      seller: venta.seller,
      items,
      payments,
      subtotal: venta.subtotal,
      discount: venta.discount,
      total: venta.total,
      notes: venta.notes,
    }, fmt);
  };

  return (
    <div className="nv-modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="nv-modal-ticket">
        <div className="nv-modal-header">
          <span>Venta registrada — Vista previa</span>
          <button className="nv-modal-close" onClick={onClose}><FiX size={16} /></button>
        </div>
        <div className="nv-modal-body">
          <div>
            <TicketPreview venta={venta} items={items} payments={payments} empresa={empresa} />
          </div>
        </div>
        <div className="nv-modal-footer">
          <button className="nv-btn-imprimir" onClick={() => handlePrint('a4')}>
            <FiPrinter size={14} /> A4
          </button>
          <button className="nv-btn-imprimir" onClick={() => handlePrint('80')}>
            <FiPrinter size={14} /> 80mm
          </button>
          <button className="nv-btn-imprimir" onClick={() => handlePrint('60')}>
            <FiPrinter size={14} /> 60mm
          </button>
          <button className="nv-btn-primary" onClick={onNuevaVenta}>
            Nueva venta
          </button>
          <button className="nv-btn-secondary" onClick={onClose}>Cerrar</button>
        </div>
      </div>
    </div>
  );
}

// ── Subcomponente: Modal de pago / crédito ───────────────────────────────────

function ModalPago({ mode, total, dueDate, onDueDateChange, discount, onDiscountChange, onConfirm, onClose }) {
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [monto, setMonto] = useState('');
  const [notas, setNotas] = useState('');

  const discAmount = Math.min(total, parseFloat(discount) || 0);
  const net    = Math.max(0, total - discAmount);
  const paid   = parseFloat(monto) || 0;
  const cambio = Math.max(0, paid - net);
  const isCredito = mode === 'CREDITO';
  const canConfirm = isCredito ? (!!dueDate && dueDate >= todayISO()) : !!selectedMethod;

  // Si el descuento cambia (o se elige forma de pago) después de que el
  // cajero ya había recibido un monto sugerido, "Monto recibido" se
  // resincroniza con el total neto — así el descuento se refleja al
  // instante y lo que se envía al confirmar siempre coincide con el total
  // real de la venta (evita "el monto pagado debe coincidir...").
  useEffect(() => {
    if (selectedMethod) setMonto(fmtMoney(net));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [net, selectedMethod]);

  const handleConfirm = () => {
    if (!canConfirm) return;
    // Se envía siempre el total neto (no lo tecleado en "Monto recibido",
    // que es solo para calcular el vuelto) — así nunca se desincroniza del
    // total real que valida el backend.
    onConfirm(isCredito ? 'CRÉDITO' : selectedMethod, isCredito ? 0 : net, notas);
  };

  return (
    <div className="nv-modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="nv-modal-pago">
        <div className="nv-modal-header">
          <span>{isCredito ? 'Venta a Crédito' : `Pagar S/ ${fmtMoney(net)}`}</span>
          <button className="nv-modal-close" onClick={onClose}><FiX size={16} /></button>
        </div>
        <div className="nv-modal-body">
          {isCredito ? (
            <>
              <div className="nv-pago-cambio">
                Total a crédito: <strong>S/ {fmtMoney(net)}</strong>
              </div>
              <div className="nv-pago-credito-note">
                La venta quedará pendiente de cobro en Cuentas por Cobrar.
              </div>
            </>
          ) : (
            <>
              <div className="nv-pago-label">Pagado Por:</div>
              <div className="nv-pago-methods">
                {PAYMENT_BUTTONS.map(pb => (
                  <button
                    key={pb.key}
                    className={`nv-pago-btn ${pb.cls} ${selectedMethod === pb.key ? 'nv-pago-btn-active' : ''}`}
                    onClick={() => setSelectedMethod(pb.key)}
                  >
                    <span className="nv-pago-btn-icon">{pb.icon}</span>
                    <span className="nv-pago-btn-label">{pb.label}</span>
                  </button>
                ))}
              </div>
              {selectedMethod && (
                <div className="nv-pago-amount-wrap">
                  <label className="nv-pago-amount-label">Monto recibido:</label>
                  <input
                    className="nv-pago-amount-input"
                    type="number"
                    min="0"
                    step="0.01"
                    value={monto}
                    onChange={e => setMonto(e.target.value)}
                    autoFocus
                  />
                </div>
              )}
              <div className="nv-pago-cambio">
                Cambio: <strong>S/ {fmtMoney(cambio)}</strong>
              </div>
            </>
          )}

          {/* Adicionales: siempre debajo de la forma de pago / fecha de vencimiento */}
          <div className="nv-pago-amount-wrap">
            <label className="nv-pago-amount-label">Descuento (S/):</label>
            <input
              className="nv-pago-amount-input"
              type="number"
              min="0"
              max={total}
              step="0.01"
              value={discount}
              onChange={e => onDiscountChange(e.target.value)}
              onFocus={e => e.target.select()}
            />
          </div>
          {isCredito && (
            <>
              <div className="nv-pago-label">Fecha de Vencimiento</div>
              <input
                className="nv-pago-amount-input nv-pago-date-input"
                type="date"
                value={dueDate}
                min={todayISO()}
                onChange={e => onDueDateChange(e.target.value)}
              />
            </>
          )}
          <div className="nv-pago-amount-wrap">
            <label className="nv-pago-amount-label">Notas:</label>
            <input
              className="nv-pago-amount-input"
              type="text"
              maxLength={200}
              value={notas}
              onChange={e => setNotas(e.target.value)}
              placeholder="Comentario para el documento de venta (opcional)"
            />
          </div>
        </div>
        <div className="nv-modal-footer">
          <button
            className="nv-btn-confirmar-pago"
            onClick={handleConfirm}
            disabled={!canConfirm}
          >
            {isCredito ? 'Generar Venta a Crédito' : 'Confirmar Venta'}
          </button>
          <button className="nv-btn-secondary" onClick={onClose}>Volver</button>
        </div>
      </div>
    </div>
  );
}

// ── Subcomponente: Modal búsqueda cliente (RENIEC/SUNAT) ─────────────────────

// Fuente badge
function SourceBadge({ source }) {
  if (!source) return null;
  const styles = {
    'local':       { bg: '#dcfce7', color: '#166534', label: '✓ Base de datos local' },
    'apis.net.pe': { bg: '#dbeafe', color: '#1e40af', label: '🌐 APIs.net.pe (SUNAT/RENIEC)' },
    'manual':      { bg: '#fef3c7', color: '#92400e', label: '✏ Registro manual' },
  };
  const s = styles[source] || styles.manual;
  return (
    <span style={{
      display:'inline-flex', alignItems:'center', gap:4,
      background: s.bg, color: s.color, fontSize:'0.75rem',
      fontWeight:600, padding:'2px 10px', borderRadius:12, marginBottom:8,
    }}>{s.label}</span>
  );
}

// ── Flujo: 1) BD local → 2) APIs.net.pe → 3) Registro manual ─────────────────
function ModalBuscarCliente({ docNumber, docType, businessId, onSelect, onClose }) {
  // phase: 'searching' | 'found_db' | 'found_api' | 'not_found' | 'error'
  const [phase,   setPhase]   = useState('searching');
  const [apiData, setApiData] = useState(null);
  const [errMsg,  setErrMsg]  = useState('');
  const [saving,  setSaving]  = useState(false);
  const isRUC = docType === 'RUC';

  // Formulario editable (pre-rellenado desde API si se encontró)
  const [form, setForm] = useState({
    nombres:           '',
    apellido_paterno:  '',
    apellido_materno:  '',
    phone:             '',
    address:           '',
  });
  const sf = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // ── Paso 1+2: buscar en BD local y luego en API ─────────────────────────
  useEffect(() => {
    (async () => {
      setPhase('searching');
      setErrMsg('');
      try {
        const res = await api.get(`/api/exchange/lookup?type=${docType}&number=${encodeURIComponent(docNumber)}`);
        const { data, not_found } = res.data;

        // Paso 1: encontrado en BD local → auto-seleccionar sin mostrar modal
        if (data && data.in_db) {
          const fullName = data.nombre_completo ||
            [data.nombres, data.apellido_paterno, data.apellido_materno].filter(Boolean).join(' ');
          onSelect({
            id:              data.id,
            document_number: data.document_number,
            document_type:   data.document_type,
            client_name:     fullName,
          });
          return; // cierra el modal automáticamente via onSelect
        }

        // Paso 2: encontrado en API pero no en BD
        if (data && !data.in_db) {
          setApiData(data);
          setForm({
            nombres:          data.nombres          || data.full_name || '',
            apellido_paterno: data.apellido_paterno || '',
            apellido_materno: data.apellido_materno || '',
            phone:            data.phone            || '',
            address:          data.address          || data.direccion || '',
          });
          setPhase('found_api');
          return;
        }

        // Paso 3: no encontrado en ninguno → formulario manual
        if (not_found || !data) {
          setPhase('not_found');
          return;
        }

        setPhase('not_found');
      } catch (e) {
        setErrMsg(e.response?.data?.error || 'Error al consultar el documento');
        setPhase('error');
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Guardar en BD y usar ─────────────────────────────────────────────────
  const handleSaveAndUse = async () => {
    if (!form.nombres.trim() && !form.apellido_paterno.trim()) {
      setErrMsg('Ingresa al menos el nombre o razón social');
      return;
    }
    setSaving(true);
    setErrMsg('');
    try {
      const payload = {
        document_type:    docType,
        document_number:  docNumber,
        nombres:          form.nombres,
        apellido_paterno: form.apellido_paterno,
        apellido_materno: form.apellido_materno,
        phone:            form.phone,
        address:          form.address,
      };
      const res = await api.post(`/api/clients?business_id=${businessId}`, payload);
      const saved = res.data.data || res.data;
      const fullName = [form.nombres, form.apellido_paterno, form.apellido_materno].filter(Boolean).join(' ');
      onSelect({
        id:              saved.insertId || saved.id,
        document_number: docNumber,
        document_type:   docType,
        client_name:     fullName || docNumber,
      });
    } catch (e) {
      setErrMsg(e.response?.data?.error || 'Error al guardar el cliente');
    } finally {
      setSaving(false);
    }
  };

  // ── Usar sin guardar ─────────────────────────────────────────────────────
  const handleUseWithout = () => {
    const fullName = [form.nombres, form.apellido_paterno, form.apellido_materno].filter(Boolean).join(' ');
    onSelect({ id: null, document_number: docNumber, document_type: docType, client_name: fullName || docNumber });
  };

  // ── Render del formulario de datos ───────────────────────────────────────
  const renderForm = () => (
    <div className="nv-cliente-form">
      {isRUC ? (
        <div className="nv-cliente-field full">
          <label>Razón Social *</label>
          <input value={form.nombres} onChange={e => sf('nombres', e.target.value)} placeholder="Razón Social de la empresa" />
        </div>
      ) : (
        <>
          <div className="nv-cliente-field">
            <label>Nombres *</label>
            <input value={form.nombres} onChange={e => sf('nombres', e.target.value)} placeholder="Nombres" />
          </div>
          <div className="nv-cliente-field">
            <label>Apellido Paterno</label>
            <input value={form.apellido_paterno} onChange={e => sf('apellido_paterno', e.target.value)} placeholder="Apellido Paterno" />
          </div>
          <div className="nv-cliente-field">
            <label>Apellido Materno</label>
            <input value={form.apellido_materno} onChange={e => sf('apellido_materno', e.target.value)} placeholder="Apellido Materno" />
          </div>
        </>
      )}
      <div className="nv-cliente-field">
        <label>Teléfono</label>
        <input value={form.phone} onChange={e => sf('phone', e.target.value)} placeholder="Teléfono" />
      </div>
      <div className="nv-cliente-field full">
        <label>Dirección</label>
        <input value={form.address} onChange={e => sf('address', e.target.value)} placeholder="Dirección" />
      </div>
    </div>
  );

  return (
    <div className="nv-modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="nv-modal-cliente">
        <div className="nv-modal-header">
          <FiUser size={16} />
          <span>Búsqueda de Cliente — {docType}: <strong>{docNumber}</strong></span>
          <button className="nv-modal-close" onClick={onClose}><FiX size={16} /></button>
        </div>

        <div className="nv-modal-body">
          {/* ── Buscando ── */}
          {phase === 'searching' && (
            <div className="nv-search-steps">
              <div className="nv-step nv-step-active">
                <span className="nv-step-dot" /> Paso 1: Buscando en base de datos local...
              </div>
              <div className="nv-step nv-step-waiting">
                <span className="nv-step-dot" /> Paso 2: Consultar SUNAT/RENIEC (APIs.net.pe)
              </div>
              <div className="nv-step nv-step-waiting">
                <span className="nv-step-dot" /> Paso 3: Registro manual
              </div>
              <div className="nv-loading" style={{marginTop:16}}>Consultando...</div>
            </div>
          )}

          {/* ── Encontrado en API ── */}
          {phase === 'found_api' && (
            <>
              <SourceBadge source="apis.net.pe" />
              <div className="nv-found-notice">
                Encontrado en {docType === 'DNI' ? 'RENIEC' : 'SUNAT'} — no está en tu base de datos.
                Puedes guardarlo o usarlo sin guardar.
              </div>
              {renderForm()}
            </>
          )}

          {/* ── No encontrado / manual ── */}
          {(phase === 'not_found' || phase === 'error') && (
            <>
              <SourceBadge source="manual" />
              {phase === 'not_found' && (
                <div className="nv-not-found-notice">
                  No se encontró el {docType} <strong>{docNumber}</strong> en la base de datos ni en SUNAT/RENIEC.
                  Completa los datos manualmente.
                </div>
              )}
              {errMsg && <div className="nv-error-box" style={{marginBottom:10}}>{errMsg}</div>}
              {renderForm()}
            </>
          )}

          {/* ── Error global ── */}
          {errMsg && phase !== 'not_found' && phase !== 'error' && (
            <div className="nv-error-box">{errMsg}</div>
          )}
        </div>

        {/* ── Footer con acciones ── */}
        {(phase === 'found_api' || phase === 'not_found' || phase === 'error') && (
          <div className="nv-modal-footer">
            {errMsg && <span className="nv-modal-err">{errMsg}</span>}
            <button className="nv-btn-primary" onClick={handleSaveAndUse} disabled={saving}>
              {saving ? 'Guardando...' : '💾 Guardar en BD y usar'}
            </button>
            <button className="nv-btn-secondary" onClick={handleUseWithout} disabled={saving}>
              Usar sin guardar
            </button>
            <button className="nv-btn-secondary" onClick={onClose} disabled={saving}>Cancelar</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Subcomponente: Modal registro rápido de cliente (mínimos datos) ─────────
function ModalNuevoCliente({ docTypes, businessId, onSaved, onClose }) {
  const [form, setForm] = useState({
    document_type:    '',
    document_number:  '',
    nombres:          '',
    apellido_paterno: '',
    apellido_materno: '',
    address:          '',
  });
  const [errMsg, setErrMsg] = useState('');
  const [saving, setSaving] = useState(false);
  const isRUC = form.document_type.includes('RUC');
  const isDNI = form.document_type.includes('DNI');

  const sf = (k, v) => { setForm(f => ({ ...f, [k]: v })); setErrMsg(''); };

  const handleDocTypeChange = (e) => {
    const val = e.target.value;
    setForm(f => ({
      ...f,
      document_type:   val,
      document_number: '',
      ...(val.includes('RUC') ? { apellido_paterno: '', apellido_materno: '' } : {}),
    }));
    setErrMsg('');
  };

  const handleDocNumber = (e) => {
    const raw = e.target.value;
    const val = (isDNI || isRUC) ? raw.replace(/\D/g, '') : raw;
    sf('document_number', val);
  };

  const handleSave = async () => {
    if (!form.document_type) { setErrMsg('Selecciona el tipo de documento'); return; }
    if (!form.document_number.trim()) { setErrMsg('Ingresa el número de documento'); return; }
    if (isDNI && form.document_number.length !== 8) { setErrMsg('El DNI debe tener 8 dígitos'); return; }
    if (isRUC && form.document_number.length !== 11) { setErrMsg('El RUC debe tener 11 dígitos'); return; }
    if (!form.nombres.trim()) { setErrMsg(isRUC ? 'Ingresa la razón social' : 'Ingresa el nombre'); return; }
    if (!form.address.trim()) { setErrMsg('Ingresa el domicilio'); return; }

    setSaving(true);
    setErrMsg('');
    try {
      const payload = {
        document_type:    form.document_type,
        document_number:  form.document_number.trim(),
        nombres:          form.nombres.trim(),
        apellido_paterno: form.apellido_paterno.trim(),
        apellido_materno: form.apellido_materno.trim(),
        address:          form.address.trim(),
      };
      const res = await api.post(`/api/clients?business_id=${businessId}`, payload);
      const saved = res.data.data || res.data;
      const fullName = [form.nombres, form.apellido_paterno, form.apellido_materno]
        .map(s => s.trim()).filter(Boolean).join(' ');
      onSaved({
        id:              saved.insertId || saved.id,
        document_number: payload.document_number,
        document_type:   payload.document_type,
        client_name:     fullName || payload.document_number,
      });
    } catch (e) {
      setErrMsg(e.response?.data?.error || 'Error al guardar el cliente');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="nv-modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="nv-modal-cliente">
        <div className="nv-modal-header">
          <FiPlus size={16} />
          <span>Registrar Cliente</span>
          <button className="nv-modal-close" onClick={onClose}><FiX size={16} /></button>
        </div>

        <div className="nv-modal-body">
          <div className="nv-cliente-form">
            <div className="nv-cliente-field">
              <label>Tipo Documento *</label>
              <select value={form.document_type} onChange={handleDocTypeChange}>
                <option value="">Seleccione...</option>
                {docTypes.map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
              </select>
            </div>
            <div className="nv-cliente-field">
              <label>N° Documento *{isDNI && ' (8 dígitos)'}{isRUC && ' (11 dígitos)'}</label>
              <input
                value={form.document_number}
                onChange={handleDocNumber}
                maxLength={isDNI ? 8 : isRUC ? 11 : 20}
                inputMode={(isDNI || isRUC) ? 'numeric' : 'text'}
              />
            </div>
            <div className={`nv-cliente-field${isRUC ? ' full' : ''}`}>
              <label>{isRUC ? 'Razón Social *' : 'Nombres *'}</label>
              <input value={form.nombres} onChange={e => sf('nombres', e.target.value)} placeholder={isRUC ? 'Razón Social de la empresa' : 'Nombres'} />
            </div>
            {!isRUC && (
              <>
                <div className="nv-cliente-field">
                  <label>Apellido Paterno</label>
                  <input value={form.apellido_paterno} onChange={e => sf('apellido_paterno', e.target.value)} />
                </div>
                <div className="nv-cliente-field">
                  <label>Apellido Materno</label>
                  <input value={form.apellido_materno} onChange={e => sf('apellido_materno', e.target.value)} />
                </div>
              </>
            )}
            <div className="nv-cliente-field full">
              <label>Domicilio *</label>
              <input value={form.address} onChange={e => sf('address', e.target.value)} placeholder="Dirección" />
            </div>
          </div>
          {errMsg && <div className="nv-error-box" style={{ marginTop: 10 }}>{errMsg}</div>}
        </div>

        <div className="nv-modal-footer">
          <button className="nv-btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Guardando...' : '💾 Guardar y usar'}
          </button>
          <button className="nv-btn-secondary" onClick={onClose} disabled={saving}>Cancelar</button>
        </div>
      </div>
    </div>
  );
}

// ── Componente principal ─────────────────────────────────────────────────────

export default function NuevaVenta() {
  const { user }   = useAuth();
  const navigate   = useNavigate();
  const businessId = user?.business_id || 1;
  const userId     = user?.id;
  const username   = user?.username || '';
  // Para impresiones (ticket) se usa el nombre completo del registro del
  // usuario, no el nombre de usuario de login.
  const sellerFullName = user?.full_name || username;

  // ── Cabecera ──────────────────────────────────────────────────────────────
  const [docTypes,     setDocTypes]     = useState([]); // desde Documentos Pago (config)
  const [docType,      setDocType]      = useState('');
  const [opType]                        = useState('VENTA INTERNA');
  const [serie,        setSerie]        = useState('—');
  const [secuencia,    setSecuencia]    = useState(0);
  const [seriesLoaded, setSeriesLoaded] = useState(false);

  // ── Modo de venta: CONTADO | CREDITO ────────────────────────────────────────
  const [saleMode, setSaleMode] = useState('CONTADO');
  const [dueDate,  setDueDate]  = useState(todayISO());
  const [discount, setDiscount] = useState('0.00');

  // ── Cliente (búsqueda con autocompletado) ───────────────────────────────────
  const [clientSearch,    setClientSearch]    = useState('');
  const [clientOptions,   setClientOptions]   = useState([]);
  const [showClientDrop,  setShowClientDrop]  = useState(false);
  const [searchingClient, setSearchingClient] = useState(false);
  const [selectedClient,  setSelectedClient]  = useState(null);
  const [showExternalModal, setShowExternalModal] = useState(false);
  const [showNewClientModal, setShowNewClientModal] = useState(false);
  const [clientDocTypes, setClientDocTypes] = useState([]);
  const clientBoxRef = useRef(null);

  // Nombre opcional para el ticket — solo aplica en ventas al contado sin
  // cliente seleccionado (por defecto "Clientes Varios"). Si se elige un
  // cliente, el nombre del ticket sale del registro de clientes y este
  // campo se deshabilita.
  const [ticketClientName, setTicketClientName] = useState('');

  // ── Empresa (para el ticket) ────────────────────────────────────────────────
  const [empresa, setEmpresa] = useState(null);

  // ── Items del detalle ─────────────────────────────────────────────────────
  const [items, setItems] = useState([]);
  // Refs para autofoco al agregar un producto y para la secuencia
  // Enter/Tab entre campos (Kg → Und → P. Unit.) de cada fila.
  const kgInputRefs = useRef([]);
  const undInputRefs = useRef([]);
  const priceInputRefs = useRef([]);
  const prevItemsLengthRef = useRef(0);

  // ── Catálogo de productos ──────────────────────────────────────────────────
  const [catalog,      setCatalog]      = useState([]);
  const [catalogSearch, setCatalogSearch] = useState('');
  const [catalogPage,  setCatalogPage]  = useState(1);
  const [warehouseId,  setWarehouseId]  = useState(null);
  const [warehouseName, setWarehouseName] = useState('');
  const [loadingCatalog, setLoadingCatalog] = useState(false);

  // ── Formas de pago ──────────────────────────────────────────────────────
  const [paymentMethods, setPaymentMethods] = useState([]);

  // ── Caja activa ────────────────────────────────────────────────────────────
  const [cashRegisterId, setCashRegisterId] = useState(null);

  // ── Modales ────────────────────────────────────────────────────────────────
  const [showPagoModal,   setShowPagoModal]   = useState(false);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [ticketData,      setTicketData]      = useState(null);

  // ── Estado de guardado ─────────────────────────────────────────────────────
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');

  // ── Carga inicial ──────────────────────────────────────────────────────────
  useEffect(() => {
    // Cargar caja activa, almacén, formas de pago, documentos de pago y empresa.
    // La caja activa se pide siempre al backend, filtrada por el usuario del JWT
    // — nunca se confía en localStorage a solas (podría quedar la caja de otro
    // usuario si el navegador es compartido, o una ya cerrada/reasignada).
    const init = async () => {
      try {
        const [crRes, cfgRes, docRes, empRes, clientDocRes] = await Promise.all([
          getMyOpenCashRegister().catch(() => null),
          api.get(`/api/config/formas-pago?business_id=${businessId}`),
          api.get(`/api/config/docs-pago?business_id=${businessId}`),
          getEmpresaInfo(businessId),
          api.get(`/api/config/tipos-documento?business_id=${businessId}`).catch(() => null),
        ]);

        // Toda venta queda amarrada a la caja abierta del vendedor — sin caja
        // abierta no se puede vender, ni siquiera como admin (mismo criterio
        // que valida el backend en Sale.create).
        const myReg = crRes?.data?.data || null;
        setCashRegisterId(myReg?.id || null);
        if (myReg?.warehouse_id) {
          setWarehouseId(myReg.warehouse_id);
          setWarehouseName(myReg.warehouse_name || '');
        }

        setPaymentMethods(cfgRes.data?.data || []);
        const docs = docRes.data?.data || [];
        setDocTypes(docs);
        // Por defecto abre como Nota de Pedido (NP) — no con el primer
        // documento alfabético, que podía caer en NC (Nota de Crédito).
        const defaultDoc = docs.find(d => d.name === 'NP') || docs[0];
        if (defaultDoc) setDocType(defaultDoc.name);
        setEmpresa(empRes.data?.data || null);
        setClientDocTypes(clientDocRes?.data?.data || []);
      } catch {}
    };
    init();
  }, [businessId, user?.role]);

  // Cargar series cuando cambia el tipo de documento
  useEffect(() => {
    if (!docType) { setSeriesLoaded(false); return; }
    const loadSeries = async () => {
      setSeriesLoaded(false);
      try {
        const res = await api.get(`/api/sales/series?business_id=${businessId}`);
        const seriesData = res.data?.data || [];
        const found = seriesData.find(s => s.document_type === docType);
        if (found) {
          setSerie(found.serie || '—');
          setSecuencia(parseInt(found.sequence || found.next_secuencia || found.secuencia || 1));
        } else {
          setSerie('—');
          setSecuencia(0);
        }
      } catch {
        setSerie('—');
        setSecuencia(0);
      } finally {
        setSeriesLoaded(true);
      }
    };
    loadSeries();
  }, [docType, businessId]);

  // Cargar catálogo cuando cambia el almacén o la búsqueda
  const loadCatalog = useCallback(async () => {
    if (!warehouseId) return;
    setLoadingCatalog(true);
    try {
      const res = await api.get(
        `/api/warehouses/${warehouseId}/stock?business_id=${businessId}` +
        (catalogSearch ? `&search=${encodeURIComponent(catalogSearch)}` : '')
      );
      setCatalog(res.data?.data || res.data?.items || []);
      setCatalogPage(1);
    } catch {
      setCatalog([]);
    } finally {
      setLoadingCatalog(false);
    }
  }, [warehouseId, businessId, catalogSearch]);

  useEffect(() => {
    const t = setTimeout(loadCatalog, 300);
    return () => clearTimeout(t);
  }, [loadCatalog]);

  // ── Búsqueda de cliente con autocompletado (debounced) ──────────────────────
  useEffect(() => {
    if (selectedClient) return;
    const q = clientSearch.trim();
    if (q.length < 2) { setClientOptions([]); setShowClientDrop(false); return; }
    setSearchingClient(true);
    const t = setTimeout(async () => {
      try {
        const res = await api.get(`/api/clients?business_id=${businessId}&search=${encodeURIComponent(q)}`);
        setClientOptions((res.data?.data || []).slice(0, 8));
        setShowClientDrop(true);
      } catch {
        setClientOptions([]);
      } finally {
        setSearchingClient(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [clientSearch, selectedClient, businessId]);

  // Cerrar el dropdown al hacer click fuera
  useEffect(() => {
    const handler = (e) => {
      if (clientBoxRef.current && !clientBoxRef.current.contains(e.target)) {
        setShowClientDrop(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── F2 — activar modo crédito ──────────────────────────────────────────────
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'F2') {
        e.preventDefault();
        setSaleMode('CREDITO');
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // ── Cálculos ───────────────────────────────────────────────────────────────
  const totals = useMemo(() => {
    const subtotalBruto = items.reduce((s, it) => {
      const q = parseFloat(it.quantity || 0);
      const p = parseFloat(it.unit_price || 0);
      return s + q * p;
    }, 0);
    const totalDesc = items.reduce((s, it) => s + parseFloat(it.discount || 0), 0);
    const subtotal  = subtotalBruto;
    const total     = Math.max(0, subtotal - totalDesc);
    return { subtotalBruto, subtotal, totalDesc, total };
  }, [items]);

  // ── Catálogo paginado ──────────────────────────────────────────────────────
  const catalogTotalPages = Math.max(1, Math.ceil(catalog.length / ITEMS_PER_PAGE));
  const catalogPaged = catalog.slice(
    (catalogPage - 1) * ITEMS_PER_PAGE,
    catalogPage       * ITEMS_PER_PAGE
  );

  // ── Agregar producto al detalle ─────────────────────────────────────────────
  const addProduct = (p) => {
    const stock    = parseFloat(p.stock || p.stock_quantity || 0);
    const price    = parseFloat(p.sale_price || p.price || p.unit_price || 0);
    const code     = p.sku || p.code || '';
    const um       = p.unit_measure || p.unidad_medida || 'KG';

    // Cada clic agrega una línea nueva — un mismo producto puede repetirse
    // varias veces en el detalle (ej. varios pesos distintos de un mismo
    // producto vendido por Kg, como Pollo Carne).
    setItems(prev => [...prev, {
      product_id:   p.id,
      code,
      product_name: p.name || p.product_name || '',
      unit_measure: um,
      baseUnitMeasure: um,
      baseUnitPrice:   parseFloat(price),
      factor:          1,
      presentationName: null,
      presentationOptions: p.presentations || [],
      special_condition: !!p.special_condition,
      quantity:     0,
      qty_unidad:   0,
      stock,
      stock_unidad: parseFloat(p.stock_unidad || 0),
      unit_price:   parseFloat(price).toFixed(2),
      discount:     '0.00',
      total:        '0.00',
    }]);
  };

  // Al agregar una línea nueva, el campo Kg queda enfocado y seleccionado
  // por defecto para que el usuario pueda escribir el valor de inmediato.
  useEffect(() => {
    if (items.length > prevItemsLengthRef.current) {
      const idx = items.length - 1;
      requestAnimationFrame(() => {
        const el = kgInputRefs.current[idx];
        if (el) { el.focus(); el.select(); }
      });
    }
    prevItemsLengthRef.current = items.length;
  }, [items.length]);

  // Secuencia con Enter (y Tab, que ya sigue el orden natural del DOM):
  // Kg → Und (si el producto es de doble variable) → P. Unit.
  const focusAndSelect = (el) => { if (el) { el.focus(); el.select(); } };
  const handleKgEnter = (e, idx, hasUnd) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    focusAndSelect(hasUnd ? undInputRefs.current[idx] : priceInputRefs.current[idx]);
  };
  const handleUndEnter = (e, idx) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    focusAndSelect(priceInputRefs.current[idx]);
  };
  const handlePriceEnter = (e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    e.target.blur();
  };

  // ── Cambiar presentación de venta de una línea del carrito ─────────────────
  const changePresentation = (idx, presKey) => {
    setItems(prev => prev.map((it, i) => {
      if (i !== idx) return it;
      let factor = 1, unit_measure = it.baseUnitMeasure, unit_price = it.baseUnitPrice, presentationName = null;
      if (presKey !== 'base') {
        const pres = (it.presentationOptions || []).find(pp => String(pp.id) === String(presKey));
        if (pres) {
          factor = parseInt(pres.factor, 10) || 1;
          unit_measure = pres.name;
          unit_price = parseFloat(pres.sale_price);
          presentationName = pres.name;
        }
      }
      const disc = parseFloat(it.discount || 0);
      const maxQty = factor > 0 ? Math.floor((parseFloat(it.stock) || 0) / factor) : parseFloat(it.quantity);
      const currentQty = parseFloat(it.quantity);
      const baseQty = Number.isNaN(currentQty) ? 1 : currentQty;
      const qty = Math.min(baseQty, maxQty > 0 ? maxQty : (baseQty || 1));
      return {
        ...it,
        factor, unit_measure, presentationName,
        unit_price: parseFloat(unit_price).toFixed(2),
        quantity: qty,
        total: fmtMoney(Math.max(0, qty * parseFloat(unit_price) - disc)),
      };
    }));
  };

  // ── Actualizar item ────────────────────────────────────────────────────────
  const updateItem = (idx, field, val) => {
    setItems(prev => prev.map((it, i) => {
      if (i !== idx) return it;
      const updated = { ...it, [field]: val };
      const qty  = parseFloat(updated.quantity  || 0);
      const price= parseFloat(updated.unit_price || 0);
      const disc = parseFloat(updated.discount   || 0);
      updated.total = fmtMoney(Math.max(0, qty * price - disc));
      return updated;
    }));
  };

  const removeItem = (idx) => setItems(prev => prev.filter((_, i) => i !== idx));

  // ── Cliente: seleccionar de la lista de autocompletado ──────────────────────
  const handleSelectClientOption = (cli) => {
    const fullName = cli.nombre_completo ||
      [cli.nombres, cli.apellido_paterno, cli.apellido_materno].filter(Boolean).join(' ');
    setSelectedClient({
      id:              cli.id,
      document_number: cli.document_number,
      document_type:   cli.document_type,
      client_name:     fullName,
    });
    setClientSearch('');
    setClientOptions([]);
    setShowClientDrop(false);
    setTicketClientName('');
  };

  const handleClientSelectFromExternal = (client) => {
    setSelectedClient(client);
    setClientSearch('');
    setShowExternalModal(false);
    setTicketClientName('');
  };

  const clearClient = () => {
    setSelectedClient(null);
    setClientSearch('');
  };

  const handleNewClientSaved = (client) => {
    setSelectedClient(client);
    setClientSearch('');
    setShowNewClientModal(false);
    setTicketClientName('');
  };

  // Detecta si lo tecleado parece un DNI (8 dígitos) o RUC (11 dígitos)
  const digitsOnly     = clientSearch.replace(/\D/g, '');
  const looksLikeDoc   = digitsOnly.length === 8 || digitsOnly.length === 11;
  const externalDocType = digitsOnly.length === 11 ? 'RUC' : 'DNI';

  // Nota de Crédito: no es una venta al contado/crédito normal — no descuenta
  // stock (lo devuelve al almacén) y no genera deuda nueva (abona la deuda
  // existente del cliente). Por eso se registra directo, sin pasar por el
  // modal de pago (que solo tiene sentido para ventas reales).
  const isCreditNote = isCreditNoteDocType(docType);

  // ── Modal pago / crédito ─────────────────────────────────────────────────────
  const handleOpenPago = () => {
    if (!cashRegisterId) {
      setError('Debes abrir una caja antes de registrar una venta.');
      return;
    }
    if (items.length === 0) {
      setError('Agrega al menos un producto antes de continuar.');
      return;
    }
    if (saleMode === 'CREDITO' && !selectedClient) {
      setError('Selecciona un cliente para ventas a crédito.');
      return;
    }
    if (isCreditNote) {
      if (!selectedClient) {
        setError('Selecciona el cliente al que se le aplicará la Nota de Crédito.');
        return;
      }
      setError('');
      handleGuardar('NOTA DE CRÉDITO', totals.total);
      return;
    }
    setError('');
    setShowPagoModal(true);
  };

  // ── Guardar venta ──────────────────────────────────────────────────────────
  const handleGuardar = async (paymentMethod, amountPaid, notas = '') => {
    setShowPagoModal(false);
    setSaving(true);
    setError('');

    const isCredit  = saleMode === 'CREDITO';
    const discAmount = Math.min(totals.total, parseFloat(discount) || 0);
    const netTotal   = Math.max(0, totals.total - discAmount);
    // Solo aplica al contado y sin cliente seleccionado — el backend también
    // lo valida así, esto es solo para no mandar basura innecesaria.
    const effectiveTicketName = (!isCredit && !selectedClient) ? ticketClientName.trim() : '';

    try {
      // Resolver payment_method_id
      let paymentMethodId = null;
      if (!isCredit && !isCreditNote && paymentMethods.length > 0) {
        const pm = paymentMethods.find(
          m => m.name?.toUpperCase() === paymentMethod.toUpperCase() ||
               m.short_name?.toUpperCase() === paymentMethod.toUpperCase()
        );
        paymentMethodId = pm?.id || null;
      }

      const payload = {
        document_type:    docType,
        operation_type:   opType,
        currency:         'SOLES',
        exchange_rate:    1,
        client_id:        selectedClient?.id || null,
        ticket_client_name: effectiveTicketName || null,
        due_date:         isCredit ? (dueDate || null) : null,
        notes:            notas ? notas.trim() : null,
        is_paid:          !isCredit,
        payment_status:   isCredit ? 'credit' : 'paid',
        warehouse_id:     warehouseId || null,
        cash_register_id: isCreditNote ? null : (cashRegisterId || null),
        seller_id:        userId || null,
        discount:         discAmount,
        items: items.map(it => {
          const factor = it.factor || 1;
          const base = {
            product_id:   it.product_id,
            barcode:      it.code,
            product_name: it.product_name,
            unit_measure: it.baseUnitMeasure || it.unit_measure,
            quantity:     parseFloat(it.quantity) * factor,
            qty_unidad:   it.special_condition ? parseInt(it.qty_unidad || 0, 10) : 0,
            unit_price:   factor > 1 ? parseFloat(it.unit_price) / factor : parseFloat(it.unit_price),
            discount:     0,
          };
          if (factor > 1) {
            base.quantity_presentation   = parseFloat(it.quantity);
            base.unit_price_presentation = parseFloat(it.unit_price);
            base.presentation_factor     = factor;
            base.presentation_name       = it.presentationName;
          }
          return base;
        }),
        payments: (isCredit || isCreditNote) ? [] : [{
          payment_method_id: paymentMethodId,
          payment_method:    paymentMethod,
          amount:            parseFloat(amountPaid) || netTotal,
          is_credit:         false,
        }],
      };

      const { data: createRes } = await api.post(`/api/sales?business_id=${businessId}`, payload);
      const created = createRes?.data || {};

      // Mostrar ticket
      setTicketData({
        venta: {
          document_type:   docType,
          serie:           created.serie || serie,
          secuencia:       created.sequence ?? secuencia,
          client_name:     selectedClient?.client_name || effectiveTicketName || 'CLIENTES VARIOS',
          document_number: selectedClient?.document_number || '',
          seller:          sellerFullName,
          subtotal:        totals.total,
          discount:        discAmount,
          total:           netTotal,
          notes:           notas ? notas.trim() : '',
        },
        items:    [...items],
        payments: isCreditNote
          ? [{ method: 'NOTA DE CRÉDITO' }]
          : isCredit
            ? [{ method: 'CRÉDITO' }]
            : [{ method: paymentMethod, amount: amountPaid }],
      });
      setShowTicketModal(true);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Error al guardar la venta');
    } finally {
      setSaving(false);
    }
  };

  // ── Limpiar formulario para nueva venta ────────────────────────────────────
  const handleNuevaVenta = () => {
    setShowTicketModal(false);
    setTicketData(null);
    setItems([]);
    setSelectedClient(null);
    setClientSearch('');
    setTicketClientName('');
    setDueDate(todayISO());
    setDiscount('0.00');
    setSaleMode('CONTADO');
    setError('');
    // Restaurar el tipo de documento por defecto (NP) — sin esto, quedaba
    // el que se haya usado en la venta recién cerrada (ej. FACTURA/BOLETA).
    const defaultDoc = docTypes.find(d => d.name === 'NP') || docTypes[0];
    if (defaultDoc) setDocType(defaultDoc.name);
    // Incrementar secuencia optimistamente
    setSecuencia(prev => prev + 1);
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="nv-container">

      {/* ── CABECERA ─────────────────────────────────────────────────────── */}
      <div className="nv-header">
        <div className="nv-header-row1">
          <button className="nv-btn-volver" onClick={() => navigate('/ventas')}>
            <FiArrowLeft size={14} /> Volver
          </button>

          <div className="nv-header-client-wrap" ref={clientBoxRef}>
            <FiUser size={15} className="nv-header-user-icon" />
            {selectedClient ? (
              <div className="nv-header-client-name">
                {selectedClient.document_number ? `${selectedClient.document_number} — ` : ''}
                {selectedClient.client_name}
                <button type="button" className="nv-client-clear-btn" onClick={clearClient} title="Quitar cliente">
                  <FiX size={11} />
                </button>
              </div>
            ) : (
              <div className="nv-client-search-wrap">
                <FiSearch size={12} className="nv-client-search-icon" />
                <input
                  className="nv-hdr-input nv-hdr-client-input"
                  placeholder="Buscar cliente por DNI, RUC o nombre..."
                  value={clientSearch}
                  onChange={e => setClientSearch(e.target.value)}
                  onFocus={() => { if (clientOptions.length > 0) setShowClientDrop(true); }}
                  autoComplete="off"
                />
                {searchingClient && <span className="nv-client-spinner" />}
                {showClientDrop && clientOptions.length > 0 && (
                  <div className="nv-client-dropdown">
                    {clientOptions.map(cli => (
                      <div key={cli.id} className="nv-client-option" onMouseDown={() => handleSelectClientOption(cli)}>
                        <span className="nv-client-option-name">{cli.nombre_completo}</span>
                        <span className="nv-client-option-doc">{shortDocType(cli.document_type)}: {cli.document_number}</span>
                      </div>
                    ))}
                  </div>
                )}
                {looksLikeDoc && !searchingClient && clientOptions.length === 0 && (
                  <button
                    type="button"
                    className="nv-client-external-btn"
                    onMouseDown={() => setShowExternalModal(true)}
                  >
                    <FiGlobe size={11} /> Buscar {externalDocType} {digitsOnly} en RENIEC/SUNAT
                  </button>
                )}
              </div>
            )}
            {!selectedClient && (
              <button
                type="button"
                className="nv-client-new-btn"
                onClick={() => setShowNewClientModal(true)}
                title="Registrar un cliente nuevo"
              >
                <FiPlus size={11} /> Nuevo Cliente
              </button>
            )}
          </div>

          <div className="nv-header-info">
            <span className="nv-header-user">{username}</span>
            {warehouseName && (
              <span className="nv-header-caja" title="Almacén de esta venta">{warehouseName}</span>
            )}
            {cashRegisterId && (
              <span className="nv-header-caja">Caja #{cashRegisterId}</span>
            )}
          </div>
        </div>

        <div className="nv-header-row2">
          <div className="nv-hdr-field">
            <label>Tipo Documento</label>
            <select
              className="nv-hdr-select"
              value={docType}
              onChange={e => setDocType(e.target.value)}
              disabled={docTypes.length === 0}
            >
              {docTypes.length === 0 ? (
                <option value="">Sin documentos configurados</option>
              ) : docTypes.map(d => <option key={d.id} value={d.name}>{d.name}</option>)}
            </select>
          </div>
          <div className="nv-hdr-field">
            <label>Tipo Operación</label>
            <select className="nv-hdr-select" defaultValue={OP_TYPES[0]} disabled>
              {OP_TYPES.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
          <div className="nv-hdr-field">
            <label>Serie</label>
            <input className="nv-hdr-input nv-hdr-input-sm" value={serie} readOnly />
          </div>
          <div className="nv-hdr-field">
            <label>Secuencia</label>
            <input className="nv-hdr-input nv-hdr-input-sm" value={fmtSeq(secuencia)} readOnly />
          </div>
          <div className="nv-hdr-field">
            <label>Nombre p/ Ticket (opcional)</label>
            <input
              className="nv-hdr-input nv-hdr-input-wide"
              value={selectedClient ? '' : ticketClientName}
              onChange={e => setTicketClientName(e.target.value)}
              placeholder="CLIENTES VARIOS"
              disabled={!!selectedClient || saleMode === 'CREDITO'}
              title={selectedClient
                ? 'Se usará el nombre del cliente seleccionado'
                : 'Nombre que aparecerá en el ticket en vez de "CLIENTES VARIOS"'}
              maxLength={TICKET_CLIENT_NAME_MAX_LENGTH}
            />
          </div>
          {seriesLoaded && serie === '—' && docType && (
            <div className="nv-hdr-warning">
              Sin serie configurada para "{docType}" — revisa Configuración &gt; Serie Venta
            </div>
          )}
        </div>
      </div>

      {/* ── CUERPO SPLIT ─────────────────────────────────────────────────── */}
      <div className="nv-body">

        {/* ── PANEL IZQUIERDO: detalle de venta ─────────────────────────── */}
        <div className="nv-left">
          <div className="nv-left-actions">
            {!isCreditNote && (
              <div className="nv-mode-toggle">
                <button
                  type="button"
                  className={`nv-mode-btn ${saleMode === 'CONTADO' ? 'nv-mode-active-contado' : ''}`}
                  onClick={() => setSaleMode('CONTADO')}
                >
                  CONTADO
                </button>
                <button
                  type="button"
                  className={`nv-mode-btn ${saleMode === 'CREDITO' ? 'nv-mode-active-credito' : ''}`}
                  onClick={() => setSaleMode('CREDITO')}
                  title="Activar modo crédito (F2)"
                >
                  [F2] CRÉDITO
                </button>
              </div>
            )}

            <div className="nv-left-actions-center">
              <button
                className="nv-btn-fpago"
                onClick={handleOpenPago}
                disabled={saving || docTypes.length === 0 || !cashRegisterId}
                title={!cashRegisterId ? 'Debes abrir una caja antes de registrar una venta' : undefined}
              >
                {isCreditNote ? 'Aplicar Nota de Crédito' : (saleMode === 'CREDITO' ? 'Confirmar' : 'Pagar')}
              </button>
            </div>

            <span className="nv-left-total-badge">
              Total: <strong>S/ {fmtMoney(totals.total)}</strong>
            </span>
          </div>

          {error && (
            <div className="nv-error-bar">{error}</div>
          )}

          {!cashRegisterId && (
            <div className="nv-hdr-warning">
              No tienes una caja abierta — debes abrir una caja antes de poder registrar ventas.
            </div>
          )}

          {isCreditNote && (
            <div className="nv-credito-banner">
              Nota de Crédito — no descuenta stock (lo devuelve al almacén) y no genera deuda nueva:
              abona el monto contra la deuda pendiente del cliente en Cuentas por Cobrar
              {!selectedClient && ' (selecciona un cliente)'}
            </div>
          )}

          {saleMode === 'CREDITO' && (
            <div className="nv-credito-banner">
              Modo CRÉDITO activo — la venta se registrará como cuenta por cobrar
              {!selectedClient && ' (selecciona un cliente)'}
            </div>
          )}

          {/* Tabla de items */}
          <div className="nv-items-wrap">
            <table className="nv-items-table">
              <thead>
                <tr>
                  <th>Producto</th>
                  <th style={{ textAlign: 'center' }}></th>
                  <th style={{ textAlign: 'right' }}>P. Unit.</th>
                  <th style={{ textAlign: 'right' }}>Total</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="nv-items-empty">
                      Selecciona productos del catálogo
                    </td>
                  </tr>
                ) : items.map((it, idx) => (
                  <tr key={idx} className={idx % 2 === 1 ? 'nv-item-row-odd' : undefined}>
                    <td className="nv-item-name" title={it.product_name}>
                      <span className="nv-item-barcode">{it.code}</span>
                      {it.product_name}
                    </td>
                    <td className="nv-item-qty-cell">
                      <div className={`nv-qty-dual-wrap ${it.special_condition ? 'nv-qty-dual-row' : ''}`}>
                        <div className="nv-qty-group">
                          {(it.presentationOptions && it.presentationOptions.length > 0) ? (
                            <select
                              className="nv-qty-group-select"
                              value={it.factor > 1 ? String(it.presentationOptions.find(pp => pp.name === it.unit_measure)?.id || 'base') : 'base'}
                              onChange={e => changePresentation(idx, e.target.value)}
                              title="Presentación de venta"
                            >
                              <option value="base">{it.baseUnitMeasure}</option>
                              {it.presentationOptions.map(pp => (
                                <option key={pp.id} value={pp.id}>{pp.name}</option>
                              ))}
                            </select>
                          ) : (
                            <span className="nv-qty-group-lbl">{it.baseUnitMeasure || it.unit_measure || 'KG'}</span>
                          )}
                          <input
                            ref={el => { kgInputRefs.current[idx] = el; }}
                            className="nv-qty-input"
                            type="number"
                            min={it.special_condition ? '0' : '0.001'}
                            step="0.001"
                            value={it.quantity}
                            onChange={e => updateItem(idx, 'quantity', e.target.value)}
                            onFocus={e => e.target.select()}
                            onKeyDown={e => handleKgEnter(e, idx, it.special_condition)}
                          />
                        </div>
                        {it.special_condition && (
                          <div className="nv-qty-group">
                            <span className="nv-qty-group-lbl">UND</span>
                            <input
                              ref={el => { undInputRefs.current[idx] = el; }}
                              className="nv-qty-input"
                              type="number"
                              min="0"
                              step="1"
                              value={it.qty_unidad}
                              onChange={e => updateItem(idx, 'qty_unidad', e.target.value)}
                              onFocus={e => e.target.select()}
                              onKeyDown={e => handleUndEnter(e, idx)}
                            />
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      <input
                        ref={el => { priceInputRefs.current[idx] = el; }}
                        className="nv-item-price-input"
                        type="number"
                        min="0"
                        step="0.01"
                        value={it.unit_price}
                        onChange={e => updateItem(idx, 'unit_price', e.target.value)}
                        onFocus={e => e.target.select()}
                        onKeyDown={handlePriceEnter}
                      />
                    </td>
                    <td className="nv-item-total">S/ {fmtMoney(it.total)}</td>
                    <td>
                      <button className="nv-btn-del-item" onClick={() => removeItem(idx)}>
                        <FiX size={13} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              {items.length > 0 && (
                <tfoot>
                  <tr>
                    <td colSpan={4} style={{ textAlign: 'right', fontSize: 12, color: '#64748b', padding: '7px 10px' }}>
                      Total:
                    </td>
                    <td className="nv-item-total" style={{ fontSize: 13, fontWeight: 800 }}>
                      S/ {fmtMoney(totals.total)}
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          {/* Totales */}
          <div className="nv-totals">
            <div className="nv-total-row">
              <span className="nv-total-label">SubTotal:</span>
              <span className="nv-total-val">S/ {fmtMoney(totals.subtotalBruto)}</span>
            </div>
            <div className="nv-total-row">
              <span className="nv-total-label">Total Dscto:</span>
              <span className="nv-total-val nv-total-disc">- S/ {fmtMoney(totals.totalDesc)}</span>
            </div>
            <div className="nv-total-row">
              <span className="nv-total-label">IGV (0%):</span>
              <span className="nv-total-val">S/ 0.00</span>
            </div>
            <div className="nv-total-row nv-total-grand">
              <span>TOTAL:</span>
              <span className="nv-total-amount">S/ {fmtMoney(totals.total)}</span>
            </div>
          </div>
        </div>

        {/* ── PANEL DERECHO: catálogo de productos ──────────────────────── */}
        <div className="nv-right">
          <div className="nv-catalog-header">
            <div className="nv-catalog-search-wrap">
              <FiSearch className="nv-catalog-search-icon" />
              <input
                className="nv-catalog-search-input"
                placeholder="Buscar Producto!!!"
                value={catalogSearch}
                onChange={e => setCatalogSearch(e.target.value)}
              />
            </div>
          </div>

          <div className="nv-catalog-pagination">
            <button
              className="nv-pg-btn"
              onClick={() => setCatalogPage(1)}
              disabled={catalogPage === 1}
            >|&lt;</button>
            <button
              className="nv-pg-btn"
              onClick={() => setCatalogPage(p => Math.max(1, p - 1))}
              disabled={catalogPage === 1}
            >&lt;&lt;</button>
            <span className="nv-pg-current">{catalogPage}</span>
            <button
              className="nv-pg-btn"
              onClick={() => setCatalogPage(p => Math.min(catalogTotalPages, p + 1))}
              disabled={catalogPage >= catalogTotalPages}
            >&gt;&gt;</button>
            <button
              className="nv-pg-btn"
              onClick={() => setCatalogPage(catalogTotalPages)}
              disabled={catalogPage >= catalogTotalPages}
            >&gt;|</button>
            <span className="nv-pg-info">de {catalogTotalPages}</span>
          </div>

          <div className="nv-catalog-wrap">
            {loadingCatalog ? (
              <div className="nv-loading">Cargando catálogo...</div>
            ) : (
              <table className="nv-catalog-table">
                <thead>
                  <tr>
                    <th style={{ width: 80 }}>Código</th>
                    <th>Producto</th>
                    <th style={{ width: 110, textAlign: 'right' }}>P Unid Medida</th>
                    <th style={{ width: 70, textAlign: 'right' }}>Stock</th>
                  </tr>
                </thead>
                <tbody>
                  {catalogPaged.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="nv-catalog-empty">Sin productos</td>
                    </tr>
                  ) : catalogPaged.map(p => {
                    const stock    = parseFloat(p.stock || p.stock_quantity || 0);
                    const hasStock = stock > 0;
                    const price    = parseFloat(p.sale_price || p.price || p.unit_price || 0);
                    const um       = p.unit_measure || p.unidad_medida || 'KG';
                    const code     = p.sku || p.code || '';
                    const inCart   = items.some(it => it.product_id === p.id);
                    return (
                      <tr
                        key={p.id}
                        className={`nv-catalog-row ${inCart ? 'nv-catalog-in-cart' : ''}`}
                        onClick={() => addProduct(p)}
                        title="Click para agregar al detalle"
                      >
                        <td>
                          <span className={`nv-barcode-cell ${hasStock ? 'nv-barcode-ok' : 'nv-barcode-empty'}`}>
                            {code}
                          </span>
                        </td>
                        <td className="nv-catalog-name">
                          {p.name || p.product_name || ''}
                          {inCart && <span className="nv-incart-badge"> en carrito</span>}
                        </td>
                        <td className="nv-catalog-price">
                          S/ {parseFloat(price).toFixed(2)}
                          <span className="nv-catalog-um"> {um}</span>
                        </td>
                        <td className={`nv-catalog-stock ${!hasStock ? 'nv-stock-zero' : ''}`}>
                          {stock.toFixed(2)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* ── MODALES ──────────────────────────────────────────────────────── */}

      {showExternalModal && (
        <ModalBuscarCliente
          docNumber={digitsOnly}
          docType={externalDocType}
          businessId={businessId}
          onSelect={handleClientSelectFromExternal}
          onClose={() => setShowExternalModal(false)}
        />
      )}

      {showNewClientModal && (
        <ModalNuevoCliente
          docTypes={clientDocTypes}
          businessId={businessId}
          onSaved={handleNewClientSaved}
          onClose={() => setShowNewClientModal(false)}
        />
      )}

      {showPagoModal && (
        <ModalPago
          mode={saleMode}
          total={totals.total}
          dueDate={dueDate}
          onDueDateChange={setDueDate}
          discount={discount}
          onDiscountChange={setDiscount}
          onConfirm={(method, amount, notas) => handleGuardar(method, amount, notas)}
          onClose={() => setShowPagoModal(false)}
        />
      )}

      {showTicketModal && ticketData && (
        <ModalTicket
          venta={ticketData.venta}
          items={ticketData.items}
          payments={ticketData.payments}
          empresa={empresa}
          onClose={() => navigate('/ventas')}
          onNuevaVenta={handleNuevaVenta}
        />
      )}

      {saving && (
        <div className="nv-saving-overlay">
          <div className="nv-saving-box">Guardando venta...</div>
        </div>
      )}

    </div>
  );
}
